import api, { ApiError } from './api';

/**
 * Financial Service - Phase 7 Migration
 * Consumes Phase 6 Financial API endpoints
 * All calculations handled by backend - no client-side aggregation
 */

const sanitizeObject = (obj = {}) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== null)
  );
};

class FinancialService {
  constructor() {
    this.basePath = '/financial';
  }

  /**
   * Get comprehensive financial overview data
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.previousStartDate - Previous period start date
   * @param {string} params.previousEndDate - Previous period end date
   * @returns {Promise<Object>} Overview data with current period, previous period, changes, and truck performance
   */
  async getOverviewData(params = {}) {
    try {
      const query = sanitizeObject({
        startDate: params.startDate,
        endDate: params.endDate,
        previousStartDate: params.previousStartDate,
        previousEndDate: params.previousEndDate,
        includeComparison: !!(params.previousStartDate && params.previousEndDate)
      });

      const response = await api.get(`${this.basePath}/overview`, { params: query });

      // Backend returns complete overview data
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || {
        currentPeriod: {
          totalRevenue: 0,
          totalExpenses: 0,
          netProfit: 0,
          profitMargin: 0,
          operatingRatio: 0,
          costPerMile: 0
        },
        previousPeriod: {
          totalRevenue: 0,
          totalExpenses: 0,
          netProfit: 0,
          profitMargin: 0,
          operatingRatio: 0,
          costPerMile: 0
        },
        changes: {
          revenue: 0,
          expenses: 0,
          netProfit: 0,
          profitMargin: 0,
          operatingRatio: 0,
          costPerMile: 0
        }
      };
    } catch (error) {
      console.error('Failed to fetch overview data:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get truck/vehicle performance metrics
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @returns {Promise<Array>} Array of truck performance data
   */
  async getTruckPerformance(params = {}) {
    try {
      const query = sanitizeObject({
        startDate: params.startDate,
        endDate: params.endDate
      });

      const response = await api.get(`${this.basePath}/trucks/performance`, { params: query });

      // Backend returns pre-calculated truck performance metrics
      // Note: api.get() already returns response.data, so response is the actual data object
      console.log('Truck Performance API Response:', response);

      // The API wraps the data in { success: true, data: {...} }
      // api.get() unwraps response.data, so response = { success: true, data: {...} }
      // We need to extract response.data.fleet
      const data = response?.data || response;

      // The backend returns fleet array in the data object
      if (data?.fleet) {
        return data.fleet;
      }

      // Fallback to old structure if it exists
      return data?.vehicles || data?.fleet || [];
    } catch (error) {
      console.error('Failed to fetch truck performance:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get cost breakdown by category, truck, and driver
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.groupBy - Grouping dimension: 'category', 'vehicle', 'driver'
   * @returns {Promise<Object>} Cost breakdown data
   */
  async getCostBreakdown(params = {}) {
    try {
      const query = sanitizeObject({
        startDate: params.startDate,
        endDate: params.endDate
      });

      // Use the correct expense breakdown endpoint based on groupBy parameter
      const dimension = params.groupBy || 'category';
      const endpoint = `/expenses/breakdown/${dimension}`;

      const response = await api.get(endpoint, { params: query });

      // Backend returns pre-aggregated cost breakdowns
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || {
        totalCosts: 0,
        byCategory: [],
        byVehicle: [],
        byDriver: [],
        breakdown: []
      };
    } catch (error) {
      console.error('Failed to fetch cost breakdown:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get financial trends over time
   * @param {Object} params - Query parameters
   * @param {string} params.startDate - Start date (YYYY-MM-DD)
   * @param {string} params.endDate - End date (YYYY-MM-DD)
   * @param {string} params.interval - Time interval: 'daily', 'weekly', 'monthly'
   * @returns {Promise<Object>} Trend data
   */
  async getTrends(params = {}) {
    try {
      const query = sanitizeObject({
        startDate: params.startDate,
        endDate: params.endDate,
        interval: params.interval || 'monthly'
      });

      const response = await api.get(`${this.basePath}/trends`, { params: query });

      // Note: api.get() already returns response.data, so response is the actual data object
      return response || {
        trends: [],
        summary: {}
      };
    } catch (error) {
      console.error('Failed to fetch trends:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Compare two financial periods
   * @param {Object} params - Query parameters
   * @param {string} params.currentStart - Current period start date
   * @param {string} params.currentEnd - Current period end date
   * @param {string} params.previousStart - Previous period start date
   * @param {string} params.previousEnd - Previous period end date
   * @returns {Promise<Object>} Comparison data
   */
  async comparePeriods(params = {}) {
    try {
      const query = sanitizeObject({
        currentStart: params.currentStart,
        currentEnd: params.currentEnd,
        previousStart: params.previousStart,
        previousEnd: params.previousEnd
      });

      const response = await api.get(`${this.basePath}/compare`, { params: query });

      // Note: api.get() already returns response.data, so response is the actual data object
      return response || {
        current: {},
        previous: {},
        changes: {}
      };
    } catch (error) {
      console.error('Failed to compare periods:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Clear financial dashboard cache (admin only)
   * @returns {Promise<Object>} Cache clear confirmation
   */
  async clearCache() {
    try {
      const response = await api.delete(`${this.basePath}/cache`);
      // Note: api.delete() already returns response.data, so response is the actual data object
      return response || { cleared: false };
    } catch (error) {
      console.error('Failed to clear cache:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Get monthly revenue by truck for entire year
   * @param {Object} params - Query parameters
   * @param {number} params.year - Year (YYYY), optional (defaults to current year)
   * @returns {Promise<Object>} Monthly revenue data
   */
  async getMonthlyRevenueByTruck(params = {}) {
    try {
      const query = sanitizeObject({
        year: params.year
      });

      const response = await api.get(`${this.basePath}/trucks/monthly-revenue`, { params: query });

      // Note: api.get() already returns response.data, so response is the actual data object
      console.log('Monthly Revenue by Truck API Response:', response);

      // Extract the data from the wrapped response
      const data = response?.data || response;

      return data || {
        year: new Date().getFullYear(),
        months: [],
        trucks: [],
        totals: {
          monthlyTotals: [],
          yearTotal: 0,
          averageMonthly: 0
        },
        summary: {
          totalTrucks: 0,
          activeTrucks: 0,
          highestMonth: 0,
          lowestMonth: 0
        }
      };
    } catch (error) {
      console.error('Failed to fetch monthly revenue by truck:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }

  /**
   * Check financial API health
   * @returns {Promise<Object>} Health status
   */
  async getHealth() {
    try {
      const response = await api.get(`${this.basePath}/health`);
      // Note: api.get() already returns response.data, so response is the actual data object
      return response || { healthy: false };
    } catch (error) {
      console.error('Failed to check health:', error);
      throw error instanceof ApiError ? error : ApiError.fromResponse(error);
    }
  }
}

const financialService = new FinancialService();

export default financialService;
